import type {
  BulkCreateRoomBookingsDTO,
  BulkCreateRoomBookingsResultDTO,
  CreateRoomBookingDTO,
  RoomBookingCalendarDTO,
  RoomBookingDTO,
  UpdateRoomBookingDTO,
} from "@/modules/room-bookings/domain/contracts";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export const roomBookingsApi = {
  list: (query: OffsetPageQueryDTO): Promise<OffsetPageDTO<RoomBookingDTO>> => {
    const url = new URL(`${config.apiUrl}/room-bookings`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<OffsetPageDTO<RoomBookingDTO>>(url.toString());
  },
  getCalendar: (
    start: string,
    end: string,
    filters?: { roomId?: string; status?: string },
  ): Promise<{ data: RoomBookingCalendarDTO[] }> => {
    const url = new URL(`${config.apiUrl}/room-bookings/calendar`);
    url.searchParams.set("start", start);
    url.searchParams.set("end", end);
    if (filters?.roomId) url.searchParams.set("roomId", filters.roomId);
    if (filters?.status) url.searchParams.set("status", filters.status);
    return fetcher.get<{ data: RoomBookingCalendarDTO[] }>(url.toString());
  },
  getById: (id: string): Promise<RoomBookingDTO> =>
    fetcher.get<RoomBookingDTO>(`${config.apiUrl}/room-bookings/${id}`),
  create: (
    input: CreateRoomBookingDTO,
  ): Promise<{
    id: string;
    receiptId?: string;
    credit?: {
      invoiceId: string;
      totalAmount: number;
      paidAmount: number;
      remainingAmount: number;
      status: string;
    };
  }> =>
    fetcher.post<{
      id: string;
      receiptId?: string;
      credit?: {
        invoiceId: string;
        totalAmount: number;
        paidAmount: number;
        remainingAmount: number;
        status: string;
      };
    }>(`${config.apiUrl}/room-bookings`, input),
  bulkCreate: (
    input: BulkCreateRoomBookingsDTO,
  ): Promise<BulkCreateRoomBookingsResultDTO> =>
    fetcher.post<BulkCreateRoomBookingsResultDTO>(
      `${config.apiUrl}/room-bookings/bulk-create`,
      input,
    ),
  update: (
    id: string,
    input: UpdateRoomBookingDTO,
  ): Promise<{ success: boolean }> =>
    fetcher.put<{ success: boolean }>(
      `${config.apiUrl}/room-bookings/${id}`,
      input,
    ),
  complete: (id: string): Promise<{ success: boolean }> =>
    fetcher.put<{ success: boolean }>(
      `${config.apiUrl}/room-bookings/${id}/complete`,
      {},
    ),
  cancel: (id: string): Promise<{ success: boolean }> =>
    fetcher.put<{ success: boolean }>(
      `${config.apiUrl}/room-bookings/${id}/cancel`,
      {},
    ),
  listAssistInstructors: (
    id: string,
  ): Promise<{ data: RoomBookingAssistInstructorDTO[] }> =>
    fetcher.get<{ data: RoomBookingAssistInstructorDTO[] }>(
      `${config.apiUrl}/room-bookings/${id}/assist-instructors`,
    ),
  addAssistInstructor: (
    id: string,
    input: { instructorId: string; pricePerSession?: number },
  ): Promise<{
    instructorCheckInId: string;
    instructorId: string;
    instructorName: string;
  }> =>
    fetcher.post<{
      instructorCheckInId: string;
      instructorId: string;
      instructorName: string;
    }>(`${config.apiUrl}/room-bookings/${id}/assist-instructors`, input),
  removeAssistInstructor: (
    id: string,
    checkInId: string,
  ): Promise<{ instructorCheckInId: string; instructorId: string }> =>
    fetcher.delete<{ instructorCheckInId: string; instructorId: string }>(
      `${config.apiUrl}/room-bookings/${id}/assist-instructors/${checkInId}`,
    ),
};

export type RoomBookingAssistInstructorDTO = {
  id: string;
  instructorId: string;
  instructorName: string;
  checkedInAt: string;
  customerPriceKip: number | null;
};
